<template>
    <div class="header-account__user-dropdown-content d-flex">
        <div class="dropdown-remove-arrow dropdown-remove-padding d-flex">
            <b-dropdown ref="accountMenu" right :lazy="true">
                <template slot="button-content">
                    <span class="header-account__user-dropdown-link d-block">
                        <user-profile-image v-if="me" :user="me" size="small" img-width="32" />
                    </span>
                </template>

                <b-dropdown-item :to="{ name: $options.ROUTES_PROFILE_OVERVIEW, params: { id: me.id } }">
                    {{ $t('myProfile') }}
                </b-dropdown-item>

                <b-dropdown-item :to="{ name: $options.ROUTES_SETTINGS_ACCOUNT }">
                    {{ $t('accountDetails') }}
                </b-dropdown-item>

                <b-dropdown-item
                    v-if="canViewLudumFeature($options.LUDUM_SUBSCRIPTION_FEATURES.IMPORTS)"
                    :to="{ name: $options.ROUTES_SETTINGS_INTEGRATIONS }"
                >
                    {{ $t('integrations') }}
                </b-dropdown-item>

                <b-dropdown-item :to="{ name: $options.ROUTES_SETTINGS_MY_CLUBS }">
                    {{ $t('myClubs') }}
                </b-dropdown-item>

                <b-dropdown-item v-if="canOpenBillingPage" :to="{ name: $options.ROUTES_SETTINGS_BILLING }">
                    {{ $t('billing') }}
                </b-dropdown-item>

                <b-dropdown-item v-if="userIsAthlete && !isSelectedClubIndividual" :to="{ name: $options.ROUTES_SETTINGS_PRIVACY_SETTINGS }">
                    {{ $t('privacySettings') }}
                </b-dropdown-item>

                <b-dropdown-text
                    v-for="club in activeClubs"
                    :key="club.club_id"
                    class="header-account__user-dropdown-club-item"
                >
                    <a
                        class="header-account__user-dropdown-club-text d-block"
                        @click.prevent="selectClub(club.club_id)"
                    >
                        {{ club.club.data.name }}
                    </a>

                    <router-link
                        v-if="canEditClub(club)"
                        class="header-account__user-dropdown-club-edit"
                        :to="{
                            name: $options.ROUTES_SETTINGS_EDIT_CLUB,
                            params: { id: club.club_id }
                        }"
                    >
                        <iconEdit class="d-block" />
                    </router-link>
                </b-dropdown-text>

                <b-dropdown-text class="header-account__user-dropdown-actions" v-if="!userHasNoClubs">
                    <router-link
                        tag="li"
                        class="header-account__user-dropdown-action d-block"
                        :to="{ name: $options.ROUTES_SETTINGS_MY_CLUBS, hash: '#join' }"
                    >
                        <a class="header-account__user-dropdown-action-link d-block">
                            {{ $t('joinAClub') }}
                        </a>
                    </router-link>

                    <router-link
                        tag="li"
                        class="header-account__user-dropdown-action d-block"
                        :to="{ name: $options.ROUTES_CREATE_CLUB }"
                    >
                        <a class="header-account__user-dropdown-action-link d-block">
                            {{ $t('createClub') }}
                        </a>
                    </router-link>
                </b-dropdown-text>

                <b-dropdown-item
                    class="header-account__user-dropdown-logout d-block"
                    :to="{ name: $options.ROUTES_LOGOUT }"
                >
                    <span
                        class="header-account__user-dropdown-logout-content d-flex align-items-center justify-content-center"
                    >
                        <iconLogout class="header-account__user-dropdown-logout-icon mr-2" />
                        <span class="header-account__user-dropdown-logout-text">{{ $t('logout') }}</span>
                    </span>
                </b-dropdown-item>
            </b-dropdown>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { LudumEventBus, CLOSE_MENU_DROPDOWN } from '@/event-bus';
import { LUDUM_SUBSCRIPTION_FEATURES } from '@/constants';
import {
    ROUTES_CREATE_CLUB,
    ROUTES_LOGOUT,
    ROUTES_PROFILE_OVERVIEW,
    ROUTES_SETTINGS_ACCOUNT,
    ROUTES_SETTINGS_EDIT_CLUB,
    ROUTES_SETTINGS_MY_CLUBS,
    ROUTES_SETTINGS_INTEGRATIONS,
    ROUTES_SETTINGS_BILLING,
    ROUTES_SETTINGS_PRIVACY_SETTINGS
} from '@/constants/routes';
import UserProfileImage from '@/components/helpers/UserProfileImage.vue';
import permissionMixin from '@/mixins/permissions';
import iconEdit from '@/assets/img/account/edit.svg?inline';
import iconLogout from '@/assets/img/account/header/logout.svg?inline';

export default {
    ROUTES_CREATE_CLUB,
    ROUTES_LOGOUT,
    ROUTES_PROFILE_OVERVIEW,
    ROUTES_SETTINGS_ACCOUNT,
    ROUTES_SETTINGS_EDIT_CLUB,
    ROUTES_SETTINGS_MY_CLUBS,
    ROUTES_SETTINGS_INTEGRATIONS,
    ROUTES_SETTINGS_BILLING,
    ROUTES_SETTINGS_PRIVACY_SETTINGS,

    components: {
        iconEdit,
        iconLogout,
        UserProfileImage
    },

    mixins: [permissionMixin],

    props: ['userHasNoClubs'],

    LUDUM_SUBSCRIPTION_FEATURES,

    mounted() {
        LudumEventBus.$on(CLOSE_MENU_DROPDOWN, () => {
            this.$refs.accountMenu.hide(true);
        });
    },

    beforeDestroy() {
        LudumEventBus.$off(CLOSE_MENU_DROPDOWN);
    },

    methods: {
        ...mapActions({
            fetch: 'fetchUserClubs',
            select: 'selectClub',
            checkNotificationCount: 'clubHelper/getNumberOfUnreadNotifications'
        }),

        selectClub(id) {
            if (id === this.selectedClub.club_id) {
                this.$store.dispatch('notifyInfo', { message: this.$t('youAreAlreadyInThisClub') });
                return;
            }
            this.$store.dispatch('removeAllNotifications');
            this.select({ id })
                .then(this.fetch)
                .then(this.checkNotificationCount)
                .then(() => {
                    this.$refs.accountMenu.hide(true);
                    this.$store.dispatch('notifySuccess', { message: this.$t('youHaveChangedYourClub') });
                });
        },

        canEditClub(club) {
            return _.some(club.permissions.data, ['permission.data.id', 'edit-club']);
        }
    },

    computed: {
        ...mapGetters({
            activeClubs: 'userActiveClubs',
            isSelectedClubIndividual: 'isSelectedClubIndividual',
            me: 'clubHelper/me',
            selectedClub: 'selectedClub',
            userIsAthlete: 'isCurrentUserAthleteOfSelectedClub'
        }),

        canOpenBillingPage() {
            return this.isSelectedClubIndividual || this.canManageLicense();
        }
    }
};
</script>
